import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FileX, AlertTriangle } from 'lucide-react';
import { apiService } from '../services/api';
import { Article } from '../types';
import { ShareButton, ArticleContent, PageHeader } from '../components';

export const ArticlePage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [article, setArticle] = useState<Article | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [imageSrc, setImageSrc] = useState<string | null>(null);

  useEffect(() => {
    const fetchArticle = async () => {
      if (!id) return;

      try {
        setLoading(true);
        setError(null);
        const data = await apiService.getArticle(parseInt(id, 10));
        setArticle(data);
      } catch (err) {
        console.error('Error fetching article:', err);
        setError(t('article.not_found'));
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [id]);

  // For draft articles, the <img> tag won't send the JWT — fetch via axios instead
  useEffect(() => {
    if (!article?.image_path) {
      setImageSrc(null);
      return;
    }

    let objectUrl: string | null = null;

    const fetchImage = async () => {
      if (article.status === 'published') {
        // Public image: direct URL works fine
        setImageSrc(apiService.getArticleImageUrl(article.id));
        return;
      }
      // Draft/archived: must carry the Authorization header
      try {
        const blob = await apiService.fetchArticleImageBlob(article.id);
        objectUrl = URL.createObjectURL(blob);
        setImageSrc(objectUrl);
      } catch {
        setImageSrc(null);
      }
    };

    fetchImage();

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [article?.id, article?.image_path, article?.status]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className='min-h-screen bg-church-light flex items-center justify-center'>
        <div className='text-center'>
          <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-church-brown mx-auto mb-4'></div>
          <p className='text-gray-600'>{t('article.loading_article')}</p>
        </div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className='min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center'>
        <div className='text-center bg-white rounded-2xl shadow-xl border border-gray-200 p-12 max-w-md mx-4'>
          <FileX className='w-16 h-16 mx-auto mb-4 text-gray-300' />
          <h2 className='text-2xl font-semibold text-gray-900 mb-4 font-sans'>
            {t('article.not_found')}
          </h2>
          <p className='text-gray-600 mb-6 font-sans'>{error || t('article.does_not_exist')}</p>
          <button
            onClick={() => navigate('/')}
            className='inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-church-brown to-church-dark text-white rounded-lg hover:from-church-dark hover:to-church-brown transition-all duration-200 font-medium shadow-md hover:shadow-lg font-sans'
          >
            <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M10 19l-7-7m0 0l7-7m-7 7h18' />
            </svg>
            Retour aux articles
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className='min-h-screen'>
      {/* Preview Badge for non-published articles */}
      {article.status !== 'published' && (
        <div className='bg-yellow-500 text-black px-4 py-3 text-center font-semibold shadow-lg sticky top-0 z-50 flex items-center justify-center gap-2'>
          <AlertTriangle className='w-5 h-5 shrink-0' />
          MODE PRÉVISUALISATION - Cet article n'est pas encore publié (statut: {article.status})
        </div>
      )}

      {/* Page Title */}
      <PageHeader title={article.title} />

      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12'>
        <div className='bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden'>
          <div className='p-8 md:p-12'>
            <div className='flex flex-col md:flex-row gap-8 lg:gap-12'>
              {/* Article Image - Left Side */}
              {imageSrc && (
                <div className='md:w-2/5 lg:w-1/3 flex-shrink-0'>
                  <div className='rounded-xl overflow-hidden shadow-lg sticky top-24'>
                    <img
                      src={imageSrc}
                      alt={article.title}
                      className='w-full h-auto object-cover'
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Content - Right Side */}
              <div className={`flex-1 ${imageSrc ? 'md:w-3/5 lg:w-2/3' : 'w-full'}`}>
                {/* Article Header */}
                <div className='mb-8 pb-6 border-b border-gray-200'>
                  <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4'>
                    <div className='flex items-center gap-3'>
                      <div className='px-4 py-2 bg-church-light rounded-lg'>
                        <div className='text-sm text-church-brown font-medium font-sans'>
                          {formatDate(article.published_at || article.created_at)}
                        </div>
                      </div>
                    </div>
                    <ShareButton url={`${window.location.origin}/news/${article.id}`} />
                  </div>
                </div>

                {/* Audio Player */}
                {(article.audio_files && article.audio_files.length > 0) || article.audio_path ? (
                  <div className='mb-8 pt-6 border-t border-gray-200'>
                    <h3 className='text-xl font-semibold text-gray-900 mb-4 font-sans flex items-center gap-2'>
                      <svg className='w-6 h-6 text-church-brown' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3' />
                      </svg>
                      {t('article.audio')}
                    </h3>
                    <div className='space-y-4'>
                      {article.audio_files && article.audio_files.length > 0 ? (
                        article.audio_files.map((audioFile, index) => (
                          <div key={index} className='bg-gray-50 rounded-lg p-4'>
                            <p className='text-sm font-medium text-gray-700 mb-2'>{audioFile.caption}</p>
                            <audio
                              controls
                              src={apiService.getArticleAudioUrl(article.id, index)}
                              className='w-full'
                              style={{ pointerEvents: 'auto' }}
                            >
                              {t('common.browser_no_audio')}
                            </audio>
                          </div>
                        ))
                      ) : article.audio_path ? (
                        // Fallback for old single audio format
                        <div className='bg-gray-50 rounded-lg p-4'>
                          <audio
                            controls
                            src={apiService.getArticleAudioUrl(article.id)}
                            className='w-full'
                            style={{ pointerEvents: 'auto' }}
                          >
                            {t('common.browser_no_audio')}
                          </audio>
                        </div>
                      ) : null}
                    </div>
                  </div>
                ) : null}

                {/* Article Content */}
                <div className='mb-8'>
                  <div className='prose prose-lg max-w-none'>
                    <ArticleContent
                      content={article.content}
                      className='text-gray-800 leading-relaxed font-sans'
                    />
                  </div>
                </div>

                {/* Files Section */}
                {article.files && article.files.length > 0 && (
                  <div className='mb-8 pt-6 border-t border-gray-200'>
                    <h3 className='text-xl font-semibold text-gray-900 mb-4 font-sans flex items-center gap-2'>
                      <svg className='w-6 h-6 text-church-brown' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z' />
                      </svg>
                      {t('article.attached_files')}
                    </h3>
                    <div className='flex flex-wrap gap-3'>
                      {article.files.map((file, index) => {
                        // Extract filename and clean it up (remove UUID suffix if present)
                        let fileName = file.split('/').pop() || `Fichier ${index + 1}`;
                        // Remove UUID suffix pattern: _xxxxxxxx.ext -> .ext, then reconstruct
                        const match = fileName.match(/^(.+)_[a-f0-9]{8}(\.[^.]+)$/);
                        if (match) {
                          fileName = match[1] + match[2]; // Original name + extension
                        }
                        return (
                          <a
                            key={index}
                            href={apiService.getArticleFileUrl(article.id, index)}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='inline-flex items-center gap-2 px-5 py-3 text-sm font-medium text-white bg-church-brown rounded-lg hover:bg-church-dark transition-all duration-200 shadow-md hover:shadow-lg font-sans'
                          >
                            <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' />
                            </svg>
                            {fileName}
                          </a>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Back Button */}
                <div className='mt-10 pt-8 border-t border-gray-200'>
                  <button
                    onClick={() => navigate(article.status !== 'published' ? '/admin' : '/')}
                    className='inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-church-brown to-church-dark text-white rounded-lg hover:from-church-dark hover:to-church-brown transition-all duration-200 font-medium shadow-md hover:shadow-lg font-sans'
                  >
                    <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M10 19l-7-7m0 0l7-7m-7 7h18' />
                    </svg>
                    {article.status !== 'published' ? 'Retour à l\'admin' : t('article.back_to_articles')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
